import React, { useState, useCallback, useEffect, useRef } from 'react';
import LessonHeader from './LessonHeader';
import ExerciseFooter, { FooterState } from './ExerciseFooter';

// ============ TYPES ============

export interface Character {
  id: string;
  name: string;
  avatar: string; // emoji or image URL
  color: string;
  position: 'left' | 'right';
}

export type NodeType =
  | 'dialogue'
  | 'narration'
  | 'question-mc'
  | 'question-input'
  | 'question-drag'
  | 'reveal'
  | 'chapter-break'
  | 'complete';

export interface DialogueNode {
  id: string;
  type: NodeType;
  characterId?: string;
  text?: string;
  emotion?: 'neutral' | 'happy' | 'thinking' | 'excited' | 'confused';
  // For questions
  questionText?: string;
  options?: { id: string; text: string }[];
  correctOptionId?: string;
  correctAnswer?: string;
  explanation?: string;
  xpReward?: number;
  // For reveals
  revealTitle?: string;
  revealContent?: string;
  // For chapter breaks
  chapterNumber?: number;
  chapterTitle?: string;
  // Custom component to render
  customComponent?: React.ReactNode;
}

export interface DialogueLessonProps {
  title: string;
  subject: 'english' | 'maths' | 'science';
  characters: Character[];
  nodes: DialogueNode[];
  onClose?: () => void;
}

// ============ COMPONENT ============

const DialogueLesson: React.FC<DialogueLessonProps> = ({
  title,
  subject,
  characters,
  nodes,
  onClose,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [visibleNodeIndices, setVisibleNodeIndices] = useState<number[]>([0]);
  const [completedQuestions, setCompletedQuestions] = useState<Set<string>>(new Set());
  const [incorrectAttempts, setIncorrectAttempts] = useState<Record<string, number>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [inputAnswers, setInputAnswers] = useState<Record<string, string>>({});
  const [streakCount, setStreakCount] = useState(0);
  const [totalXP, setTotalXP] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState<Record<string, string>>({});

  const currentNode = nodes[currentNodeIndex];
  const getCharacter = (id?: string) => characters.find(c => c.id === id);

  // Track which nodes have started typing to prevent re-runs
  const typedNodesRef = useRef<Set<string>>(new Set());

  // Typing animation effect
  useEffect(() => {
    const node = nodes[currentNodeIndex];
    if (!node || !node.text) return;

    // Skip if we've already typed this node
    if (typedNodesRef.current.has(node.id)) return;

    // Mark as started
    typedNodesRef.current.add(node.id);

    setIsTyping(true);
    let index = 0;
    const text = node.text;

    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(prev => ({
          ...prev,
          [node.id]: text.slice(0, index + 1)
        }));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(timer);
      }
    }, 20);

    return () => {
      clearInterval(timer);
    };
  }, [currentNodeIndex, nodes]);

  // Progress
  const progress = (currentNodeIndex + 1) / nodes.length;

  // Footer state
  const footerState = useCallback((): FooterState => {
    if (!currentNode) return 'default';
    if (currentNode.type === 'question-mc' || currentNode.type === 'question-input') {
      if (completedQuestions.has(currentNode.id)) return 'correct';
      if ((incorrectAttempts[currentNode.id] || 0) > 0) return 'incorrect';
    }
    return 'default';
  }, [currentNode, completedQuestions, incorrectAttempts]);

  // Check if can continue
  const canContinue = useCallback(() => {
    if (!currentNode) return false;
    if (isTyping) return true; // Allow skipping typing

    if (currentNode.type === 'question-mc') {
      return !!selectedAnswers[currentNode.id] || completedQuestions.has(currentNode.id);
    }
    if (currentNode.type === 'question-input') {
      return !!inputAnswers[currentNode.id]?.trim() || completedQuestions.has(currentNode.id);
    }
    return true;
  }, [currentNode, isTyping, selectedAnswers, inputAnswers, completedQuestions]);

  // Scroll to node
  const scrollToNode = useCallback((index: number) => {
    setTimeout(() => {
      const element = document.getElementById(`dialogue-node-${index}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  // Handle continue
  const handleContinue = useCallback(() => {
    if (!currentNode) return;

    // Skip typing animation
    if (isTyping && currentNode.text) {
      setDisplayedText(prev => ({ ...prev, [currentNode.id]: currentNode.text! }));
      setIsTyping(false);
      return;
    }

    // Handle questions
    if ((currentNode.type === 'question-mc' || currentNode.type === 'question-input') &&
        !completedQuestions.has(currentNode.id)) {
      // Check answer
      let isCorrect = false;

      if (currentNode.type === 'question-mc') {
        isCorrect = selectedAnswers[currentNode.id] === currentNode.correctOptionId;
      } else if (currentNode.type === 'question-input') {
        const userAnswer = inputAnswers[currentNode.id]?.trim().toLowerCase();
        const correct = currentNode.correctAnswer?.toLowerCase();
        isCorrect = userAnswer === correct;
      }

      if (isCorrect) {
        setCompletedQuestions(prev => new Set([...prev, currentNode.id]));
        setTotalXP(prev => prev + (currentNode.xpReward || 15));
        setStreakCount(prev => prev + 1);
      } else {
        setIncorrectAttempts(prev => ({
          ...prev,
          [currentNode.id]: (prev[currentNode.id] || 0) + 1
        }));
        setStreakCount(0);
      }
      return;
    }

    // Complete node - close modal
    if (currentNode.type === 'complete') {
      onClose?.();
      return;
    }

    // Advance to next node
    const nextIdx = currentNodeIndex + 1;
    if (nextIdx < nodes.length) {
      setVisibleNodeIndices(prev => [...prev, nextIdx]);
      setCurrentNodeIndex(nextIdx);
      scrollToNode(nextIdx);
    }
  }, [currentNode, isTyping, completedQuestions, selectedAnswers, inputAnswers, currentNodeIndex, nodes, scrollToNode, onClose]);

  // Handle try again
  const handleTryAgain = useCallback(() => {
    if (!currentNode) return;
    setIncorrectAttempts(prev => ({ ...prev, [currentNode.id]: 0 }));
    setSelectedAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[currentNode.id];
      return newAnswers;
    });
    setInputAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[currentNode.id];
      return newAnswers;
    });
  }, [currentNode]);

  // Get button text
  const getButtonText = () => {
    if (isTyping) return 'Skip';
    if (currentNode?.type === 'complete') return 'Finish';
    if ((currentNode?.type === 'question-mc' || currentNode?.type === 'question-input') &&
        !completedQuestions.has(currentNode.id)) {
      return 'Check';
    }
    return 'Continue';
  };

  // Render a single node
  const renderNode = (node: DialogueNode, index: number) => {
    const character = getCharacter(node.characterId);
    const isCurrentNode = index === currentNodeIndex;
    const text = displayedText[node.id] || '';

    // Dialogue bubble
    if (node.type === 'dialogue' && character) {
      const isLeft = character.position === 'left';

      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className={`flex items-start gap-3 mb-4 ${isLeft ? 'justify-start' : 'justify-end'}`}
        >
          {/* Avatar - left side */}
          {isLeft && (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: character.color }}
            >
              {character.avatar}
            </div>
          )}

          {/* Bubble - fixed width */}
          <div
            className={`w-[320px] p-4 rounded-2xl ${isLeft ? 'rounded-tl-md' : 'rounded-tr-md'}`}
            style={{
              background: isLeft ? 'rgba(59, 130, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)',
              border: `1px solid ${isLeft ? 'rgba(59, 130, 246, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
            }}
          >
            <p className="text-xs font-semibold mb-1" style={{ color: character.color }}>
              {character.name}
            </p>
            <p className="text-sm" style={{ color: '#e2e8f0' }}>
              {text}
              {isCurrentNode && isTyping && <span className="animate-pulse">|</span>}
            </p>
          </div>

          {/* Avatar - right side */}
          {!isLeft && (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: character.color }}
            >
              {character.avatar}
            </div>
          )}
        </div>
      );
    }

    // Narration
    if (node.type === 'narration') {
      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-6 text-center"
        >
          <p className="text-sm italic" style={{ color: '#94a3b8' }}>
            {text}
            {isCurrentNode && isTyping && <span className="animate-pulse">|</span>}
          </p>
        </div>
      );
    }

    // Multiple choice question
    if (node.type === 'question-mc') {
      const isCompleted = completedQuestions.has(node.id);
      const hasIncorrect = (incorrectAttempts[node.id] || 0) > 0;
      const selectedId = selectedAnswers[node.id];

      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-6 p-4 rounded-xl"
          style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <p className="text-sm font-semibold mb-4" style={{ color: '#e2e8f0' }}>
            {node.questionText}
          </p>

          <div className="grid grid-cols-2 gap-2">
            {node.options?.map((option) => {
              const isSelected = selectedId === option.id;
              const isCorrectOption = option.id === node.correctOptionId;

              let bgColor = 'rgba(255, 255, 255, 0.05)';
              let borderColor = 'rgba(255, 255, 255, 0.1)';

              if (isCompleted && isCorrectOption) {
                bgColor = 'rgba(16, 185, 129, 0.2)';
                borderColor = '#10b981';
              } else if (hasIncorrect && isSelected && !isCorrectOption) {
                bgColor = 'rgba(239, 68, 68, 0.2)';
                borderColor = '#ef4444';
              } else if (isSelected) {
                bgColor = 'rgba(99, 102, 241, 0.2)';
                borderColor = '#818cf8';
              }

              return (
                <button
                  key={option.id}
                  className="p-3 rounded-lg text-left transition-all"
                  style={{ background: bgColor, border: `2px solid ${borderColor}` }}
                  onClick={() => {
                    if (!isCompleted && !hasIncorrect) {
                      setSelectedAnswers(prev => ({ ...prev, [node.id]: option.id }));
                    }
                  }}
                  disabled={isCompleted || hasIncorrect}
                >
                  <span className="text-sm" style={{ color: '#e2e8f0' }}>{option.text}</span>
                </button>
              );
            })}
          </div>

          {/* Explanation after completion */}
          {isCompleted && node.explanation && (
            <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <p className="text-xs" style={{ color: '#10b981' }}>
                {node.explanation}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Text input question
    if (node.type === 'question-input') {
      const isCompleted = completedQuestions.has(node.id);
      const hasIncorrect = (incorrectAttempts[node.id] || 0) > 0;

      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-6 p-4 rounded-xl"
          style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <p className="text-sm font-semibold mb-4" style={{ color: '#e2e8f0' }}>
            {node.questionText}
          </p>

          <input
            type="text"
            value={inputAnswers[node.id] || ''}
            onChange={(e) => setInputAnswers(prev => ({ ...prev, [node.id]: e.target.value }))}
            placeholder="Type your answer..."
            disabled={isCompleted || hasIncorrect}
            className="w-full px-4 py-3 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: `2px solid ${isCompleted ? '#10b981' : hasIncorrect ? '#ef4444' : 'rgba(255, 255, 255, 0.1)'}`,
              color: '#e2e8f0',
            }}
          />

          {isCompleted && node.explanation && (
            <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <p className="text-xs" style={{ color: '#10b981' }}>
                {node.explanation}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Reveal/educational moment
    if (node.type === 'reveal') {
      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-6 p-5 rounded-xl text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
          }}
        >
          <div className="text-2xl mb-2">💡</div>
          {node.revealTitle && (
            <h3 className="text-lg font-bold mb-2" style={{ color: '#e2e8f0' }}>
              {node.revealTitle}
            </h3>
          )}
          <p className="text-sm" style={{ color: '#c4b5fd' }}>
            {node.revealContent}
          </p>

          {/* Custom component slot */}
          {node.customComponent && (
            <div className="mt-4">
              {node.customComponent}
            </div>
          )}
        </div>
      );
    }

    // Chapter break
    if (node.type === 'chapter-break') {
      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-8 py-8 text-center"
        >
          <div
            className="inline-block px-6 py-3 rounded-full"
            style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
          >
            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#64748b' }}>
              Chapter {node.chapterNumber}
            </p>
            <h2 className="text-lg font-bold" style={{ color: '#e2e8f0' }}>
              {node.chapterTitle}
            </h2>
          </div>
        </div>
      );
    }

    // Complete
    if (node.type === 'complete') {
      return (
        <div
          key={node.id}
          id={`dialogue-node-${index}`}
          className="my-8 py-8 text-center"
        >
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transform rotate-3"
            style={{ background: `linear-gradient(135deg, ${subject === 'english' ? '#3b82f6' : subject === 'maths' ? '#8b5cf6' : '#10b981'} 0%, ${subject === 'english' ? '#1d4ed8' : subject === 'maths' ? '#6d28d9' : '#059669'} 100%)` }}
          >
            <span className="text-4xl">⭐</span>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#e2e8f0' }}>
            Lesson Complete!
          </h1>
          <p className="text-sm mb-4" style={{ color: '#64748b' }}>TOTAL XP</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-4xl font-bold" style={{ color: '#e2e8f0' }}>{totalXP}</span>
            <span className="text-2xl">⭐</span>
          </div>
        </div>
      );
    }

    return null;
  };

  // Subject color
  const subjectColor = subject === 'english' ? '#3b82f6' : subject === 'maths' ? '#8b5cf6' : '#10b981';

  return (
    <div
      className="h-full flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        minHeight: '600px',
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-50">
        <LessonHeader
          progress={progress}
          streakCount={streakCount}
          onClose={onClose}
          mode="story"
        />
      </div>

      {/* Title bar */}
      <div
        className="px-6 py-3 flex items-center gap-3"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span
          className="px-2 py-1 rounded text-xs font-semibold uppercase"
          style={{ background: `${subjectColor}20`, color: subjectColor }}
        >
          {subject}
        </span>
        <h1 className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>
          {title}
        </h1>
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto pb-32">
        <div className="max-w-2xl mx-auto px-6 py-4">
          {visibleNodeIndices.map(idx => renderNode(nodes[idx], idx))}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <ExerciseFooter
          state={footerState()}
          buttonText={getButtonText()}
          buttonEnabled={canContinue()}
          xpAmount={currentNode?.xpReward || 15}
          isLastQuestion={currentNode?.type === 'complete'}
          onContinue={handleContinue}
          onTryAgain={handleTryAgain}
        />
      </div>
    </div>
  );
};

export default DialogueLesson;
